import { Button, DialogForm, Label, useToast } from '@ais-ui/ui-core';
import { useState } from 'react';
import { ShipList } from '@ais-ui/api-spec/config';
import { useDeleteShiplist } from '@ais-ui/api';
import { useLocalization } from '@ais-ui/locale-management';
import { markErrorHandledLocally } from '@ais-ui/custom-hooks';

interface DeleteShiplistProps {
  shiplist: ShipList;
  showDialog: boolean;
  setShowDialog: React.Dispatch<React.SetStateAction<boolean>>;
}

export function DeleteShiplist({
  shiplist,
  showDialog,
  setShowDialog,
}: DeleteShiplistProps) {
  const { messages } = useLocalization();
  const { mutate } = useDeleteShiplist();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const onDelete = () => {
    setIsSubmitting(true);
    const shiplistName = shiplist.name;

    if (!shiplist.id) {
      toast({
        className: 'delete-shiplist-error-toast',
        title: messages.filtersDeleteShiplistToastErrorTitle,
        description: `${messages.filtersDeleteShiplistToastErrorDescription} ${shiplistName}`,
        variant: 'destructive',
      });
      setShowDialog(false);
      setIsSubmitting(false);
      return;
    }

    mutate(shiplist.id, {
      onSuccess: () => {
        toast({
          className: 'delete-shiplist-success-toast',
          title: messages.filtersDeleteShiplistToastSuccessTitle,
          description: messages.filtersDeleteShiplistToastSuccessDescription,
        });

        setShowDialog(false);
        setIsSubmitting(false);
      },
      onError: (error: any) => {
        markErrorHandledLocally(error);
        if (error.response?.status === 403) {
          toast({
            className: 'delete-shiplist-error-toast',
            title: messages.filtersDeleteShiplistToastForbiddenTitle,
            description: `${messages.filtersDeleteShiplistToastForbiddenDescription} ${shiplistName}`,
            variant: 'destructive',
          });
        } else {
          toast({
            className: 'delete-shiplist-error-toast',
            title: messages.filtersDeleteShiplistToastErrorTitle,
            description: `${messages.filtersDeleteShiplistToastFailedDescription} ${shiplistName}.`,
            variant: 'destructive',
          });
        }
        setShowDialog(false);
        setIsSubmitting(false);
      },
    });
  };

  return (
    <>
      <Button
        id="shiplist-delete-button"
        variant="ghost"
        className="text-primary hover:text-primary"
        onClick={() => setShowDialog(true)}
      >
        Delete
      </Button>
      <DialogForm
        showDialog={showDialog}
        onOpenChange={setShowDialog}
        size="sm"
      >
        <DialogForm.Header>
          <DialogForm.Title>{messages.shiplistsDeleteTitle}</DialogForm.Title>
          <DialogForm.Description>
            {messages.shiplistsDeleteFilterWarning}
          </DialogForm.Description>
        </DialogForm.Header>
        <DialogForm.Body>
          <div className="grid grid-cols-[auto_minmax(50%,_1fr)] 4 gap-x-2 items-baseline">
            <Label className="font-light">
              {messages.shiplistsDeleteShiplistName}
            </Label>
            <Label htmlFor="shiplist-name">{shiplist.name}</Label>
          </div>
        </DialogForm.Body>
        <DialogForm.Footer>
          <DialogForm.Cancel disabled={isSubmitting}>
            {messages.genericCancel}
          </DialogForm.Cancel>
          <DialogForm.Submit
            disabled={isSubmitting}
            variant="destructive"
            id="shiplist-confirm-delete-button"
            onSubmit={onDelete}
          >
            {isSubmitting ? messages.genericDeleting : messages.genericDelete}
          </DialogForm.Submit>
        </DialogForm.Footer>
      </DialogForm>
    </>
  );
}
